import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ALL_COMPARATORS, type ComparatorType } from '@repo/advanced-filters';
import { notification } from '@repo/ui';
import { LocalStorage } from '@vectord/utils';

const lsInstance = LocalStorage.getInstance('store.activities.secondaryFilter');

export type TimeFilterOptionsType = {
  filterType?: ComparatorType;
  offsetValue?: string;
  fromTime?: string;
  toTime?: string;
};

export enum Entities {
  LeadAttributes = 'lead_attributes',
  LeadActivity = 'lead_activity',
}

export enum AllowedFilterKeys {
  LeadStage = 'ProspectStage',
  ActivityId = 'activity_id',
  ActivityOwner = 'OwnerId',
  ActivityTime = 'ActivityTime',
  ActivityNote = 'ActivityNote',
  ActivityUpdatedAt = 'updated_at',
  LeadFirstName = 'FirstName',
  LeadLastName = 'LastName',
}

export interface ConditionRHS {
  conditionKey: AllowedFilterKeys;
  conditionComparator: ComparatorType;
  conditionValue: string;
  subConditionKey?: AllowedFilterKeys;
  subConditionComparator?: ComparatorType;
  subConditionValue?: string | TimeFilterOptionsType;
  level: number;
}

export interface ConditionsType {
  [Entities.LeadActivity]: Record<string, ConditionRHS>;
  [Entities.LeadAttributes]: Record<string, ConditionRHS>;
}

export type SortConfig = {
  sortField: string;
  sortOrder: 'asc' | 'desc';
};

export interface SecondaryFilterSliceType {
  conditions: Record<string, ConditionsType>;
  // activityId -> sortConfig
  sortConfig?: Record<string, SortConfig>;
  selectedActivityId?: string;
  searchText: string;
}

export const initialConditions: SecondaryFilterSliceType['conditions'][string] = {
  [Entities.LeadActivity]: {
    [`${AllowedFilterKeys.ActivityId}.${AllowedFilterKeys.ActivityOwner}`]: {
      conditionKey: AllowedFilterKeys.ActivityId,
      conditionComparator: ALL_COMPARATORS.is,
      conditionValue: '',
      subConditionKey: AllowedFilterKeys.ActivityOwner,
      subConditionComparator: ALL_COMPARATORS.is,
      subConditionValue: 'any',
      level: 1,
    },
  },
  [Entities.LeadAttributes]: {
    [`${AllowedFilterKeys.LeadStage}.`]: {
      conditionKey: AllowedFilterKeys.LeadStage,
      conditionComparator: ALL_COMPARATORS.is,
      conditionValue: 'any',
      level: 1,
    },
  },
};

const initialState: SecondaryFilterSliceType = {
  conditions: {},
  sortConfig: {},
  searchText: '',
};

const savedConfig: SecondaryFilterSliceType = lsInstance?.config;

export const secondaryFilterSlice = createSlice({
  name: 'secondaryFilter',
  initialState: { ...initialState, ...savedConfig },
  reducers: {
    addSecondaryFilter: (state, action: PayloadAction<{
      entityType: Entities,
      value?: ConditionRHS,
    }>) => {
      const activityId = state.selectedActivityId;
      const { entityType, value } = action.payload;
      const objectKey = `${value?.conditionKey}.${value?.subConditionKey ?? ''}`;

      if (!activityId) {
        notification.error({
          message: 'Select an activity first',
        });
        return;
      }

      if (!state.conditions[activityId]) {
        state.conditions[activityId] = structuredClone(initialConditions);
      }

      if (value) {
        state.conditions[activityId][entityType][objectKey] = value;
      } else {
        delete state.conditions[activityId][entityType][objectKey];
      }
    },
    removeTimeFilters: (state, action: PayloadAction<{
      entityType: Entities,
    }>) => {
      const activityId = state.selectedActivityId;
      const { entityType } = action.payload;

      if (!activityId) return;

      if (state.conditions[activityId]) {
        state.conditions[activityId][entityType] = Object.fromEntries(
          Object.entries(
            state.conditions[activityId][entityType],
          ).filter(([, condition]) => !(
            typeof condition.subConditionValue === 'object' && 'filterType' in condition.subConditionValue
          )),
        );
      }
    },
    setSelectedActivityId: (state, action: PayloadAction<string>) => {
      state.selectedActivityId = action.payload;
      if (state.conditions[action.payload]) return;

      secondaryFilterSlice.caseReducers.addSecondaryFilter(state, {
        payload: {
          entityType: Entities.LeadActivity,
          value: {
            conditionKey: AllowedFilterKeys.ActivityId,
            conditionComparator: ALL_COMPARATORS.is,
            conditionValue: action.payload,
            level: 1,
          },
        },
        type: 'any',
      });
    },
    resetCurrentActivityConfig: (state) => {
      const activityId = state.selectedActivityId;
      if (!activityId) return;

      delete state.conditions[activityId];
      secondaryFilterSlice.caseReducers.setSelectedActivityId(state, {
        payload: activityId,
        type: 'any',
      });
    },
    setSortConfig: (state, action: PayloadAction<{
      activityId: string;
      sortConfig?: SortConfig;
    }>) => {
      const { activityId, sortConfig } = action.payload;
      if (!state.sortConfig) {
        state.sortConfig = {};
      }

      if (sortConfig) {
        state.sortConfig[activityId] = {
          sortField: sortConfig.sortField.split('.').pop() ?? '',
          sortOrder: sortConfig.sortOrder,
        };
      } else {
        delete state.sortConfig[activityId];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('secondaryFilter/'),
      (state) => { lsInstance.config = state; },
    );
  },
});

export const {
  addSecondaryFilter,
  removeTimeFilters,
  setSelectedActivityId,
  resetCurrentActivityConfig,
  setSortConfig,
} = secondaryFilterSlice.actions;

export default secondaryFilterSlice.reducer;
